<template>
  <div class="roster-sync-modal" data-cy="roster-sync-editor" role="dialog" aria-modal="true" aria-labelledby="roster-sync-title">
    <div class="roster-sync-modal__backdrop" @click="onBackdropClick"></div>

    <div class="roster-sync-modal__shell">
      <header class="roster-sync-modal__header">
        <div>
          <h2 id="roster-sync-title" class="roster-sync-modal__title">Sync rosters</h2>
          <p class="roster-sync-modal__subtitle">
            Members of synced rosters are automatically added and removed from this group.
          </p>
        </div>
        <button type="button"
                class="harbor-chat__icon-btn"
                :disabled="saving"
                aria-label="Close"
                @click="close">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="roster-sync-modal__body">
        <div v-if="rostersLoading" class="text-center p-m">
          <spinner></spinner>
        </div>
        <template v-else>
          <section v-if="activeSyncers.length === 0" class="roster-sync-empty">
            No rosters synced yet. Add one below to keep group members in sync automatically.
          </section>
          <section v-else class="roster-sync-list">
            <div v-for="syncer in activeSyncers"
                 :key="`syncer-${syncer.rosterId}`"
                 class="roster-sync-row"
                 data-cy="sync-row">
              <div class="roster-sync-row__main">
                <p class="roster-sync-row__name">{{ syncer.rosterName }}</p>
              </div>
              <button type="button"
                      class="btn btn-default btn-compact"
                      data-cy="sync-remove"
                      :disabled="saving"
                      @click="removeSyncer(syncer)">
                Remove
              </button>
            </div>
          </section>

          <section class="roster-sync-modal__add">
            <label for="roster-sync-add" class="roster-sync-modal__label">Add a roster</label>
            <div class="roster-sync-modal__add-row">
              <select id="roster-sync-add" data-cy="sync-add-select" v-model="pendingRosterId" class="form-control">
                <option :value="null">Choose roster...</option>
                <option v-for="r in availableRosters" :key="`avail-${r.id}`" :value="r.id">{{ r.name }}</option>
              </select>
              <button type="button"
                      class="btn btn-primary btn-compact"
                      data-cy="sync-add-btn"
                      :disabled="saving || !pendingRosterId"
                      @click="addRoster">
                Add
              </button>
            </div>
          </section>

          <section v-if="activeSyncers.length > 0" class="roster-sync-modal__members">
            <label class="roster-sync-modal__label">Who gets added from each roster?</label>
            <p class="roster-sync-modal__hint">
              We'll keep this group in sync as roster membership changes.
            </p>
            <div class="roster-sync-modal__member-option">
              <input type="radio"
                     id="roster-sync-members-wrestlers-and-parents"
                     value="wrestlers_and_parents"
                     v-model="memberType" />
              <label for="roster-sync-members-wrestlers-and-parents">Wrestlers and their parents</label>
            </div>
            <div class="roster-sync-modal__member-option">
              <input type="radio"
                     id="roster-sync-members-wrestlers-only"
                     value="wrestlers_only"
                     v-model="memberType" />
              <label for="roster-sync-members-wrestlers-only">Wrestlers only</label>
            </div>
            <div class="roster-sync-modal__member-option">
              <input type="radio"
                     id="roster-sync-members-parents-only"
                     value="parents_only"
                     v-model="memberType" />
              <label for="roster-sync-members-parents-only">Parents only</label>
            </div>
          </section>
        </template>

        <p v-if="error" class="text-danger m-t-sm m-b-none">{{ errorMessage }}</p>
      </div>

      <footer class="roster-sync-modal__footer">
        <small v-if="hasPendingChanges" class="roster-sync-modal__footer-note">Unsaved changes</small>
        <button type="button" class="btn btn-default" :disabled="saving" @click="close">Cancel</button>
        <button type="button"
                class="btn btn-primary"
                data-cy="sync-save"
                :disabled="saving || !hasPendingChanges"
                @click="save">
          <spinner v-if="saving" :inline="true"></spinner>
          Save
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import Spinner from './spinner.vue';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'roster-sync-modal',
  components: { Spinner },
  mixins: [errorableMix],
  props: {
    group: { type: Object, required: true },
  },
  data() {
    return {
      rostersLoading: false,
      saving: false,
      allRosters: [],
      syncers: [],
      removedSyncerIds: [],
      pendingRosterId: null,
      // A single member-type choice applies to every synced roster. The data
      // model supports per-roster flags, but we keep the UI simple and uniform.
      memberType: 'wrestlers_and_parents',
    };
  },
  computed: {
    activeSyncers() {
      return this.syncers.filter((s) => !s.markedForRemoval);
    },
    linkedRosterIds() {
      return this.activeSyncers.map((s) => s.rosterId);
    },
    availableRosters() {
      return this.allRosters.filter((r) => !this.linkedRosterIds.includes(r.id));
    },
    hasPendingChanges() {
      const originals = (this.group.message_group_roster_syncers || []);
      const originalById = {};
      originals.forEach((o) => { originalById[o.id] = o; });

      if (this.removedSyncerIds.length > 0) return true;
      for (const s of this.syncers) {
        if (s.markedForRemoval) continue;
        if (!s.id) return true;
        const original = originalById[s.id];
        if (!original) return true;
        // The global member-type is applied to every roster on save, so a change
        // is pending if any existing syncer's stored scope differs from it.
        const originalType = this.memberTypeFrom(
          original.include_wrestlers,
          original.include_guardians,
        );
        if (originalType !== this.memberType) return true;
      }
      return false;
    },
  },
  created() {
    this.seedSyncers();
    this.fetchRosters();
    document.body.classList.add('composer-overlay-open');
    document.addEventListener('keydown', this.handleKeydown);
  },
  destroyed() {
    document.body.classList.remove('composer-overlay-open');
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    seedSyncers() {
      const existing = this.group.message_group_roster_syncers || [];
      this.syncers = existing.map((s) => ({
        id: s.id,
        rosterId: s.roster.id,
        rosterName: s.roster.name,
        markedForRemoval: false,
      }));

      // Seed the single member-type control from the existing syncers. Legacy
      // groups may mix per-roster scopes; when they disagree we fall back to the
      // default rather than silently picking one roster's setting.
      if (existing.length > 0) {
        const types = existing.map((s) => this.memberTypeFrom(s.include_wrestlers, s.include_guardians));
        const allSame = types.every((t) => t === types[0]);
        this.memberType = allSame ? types[0] : 'wrestlers_and_parents';
      }
    },
    memberTypeFrom(includeWrestlers, includeGuardians) {
      if (includeWrestlers && !includeGuardians) return 'wrestlers_only';
      if (!includeWrestlers && includeGuardians) return 'parents_only';
      return 'wrestlers_and_parents';
    },
    scopeForMemberType(memberType) {
      return {
        include_wrestlers: memberType !== 'parents_only',
        include_guardians: memberType !== 'wrestlers_only',
      };
    },
    fetchRosters() {
      const vm = this;
      vm.rostersLoading = true;
      const url = `${vm.$apiService.rostersUrl()}?per_page=200`;
      axios.get(url)
        .then((response) => {
          vm.rostersLoading = false;
          const rosters = response.data.rosters || [];
          vm.allRosters = rosters
            .filter((r) => !r.archived)
            .map((r) => ({ id: r.id, name: r.name }));
        })
        .catch(() => {
          vm.rostersLoading = false;
          vm.errorMessage = 'Unable to load rosters';
          vm.error = true;
        });
    },
    handleKeydown(e) {
      if (this.saving) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    },
    onBackdropClick() {
      if (this.saving) return;
      this.close();
    },
    close() {
      if (this.saving) return;
      this.$emit('close');
    },
    addRoster() {
      if (!this.pendingRosterId) return;
      const roster = this.allRosters.find((r) => r.id === this.pendingRosterId);
      if (!roster) return;
      this.syncers.push({
        id: null,
        rosterId: roster.id,
        rosterName: roster.name,
        markedForRemoval: false,
      });
      this.pendingRosterId = null;
    },
    removeSyncer(syncer) {
      if (syncer.id) {
        syncer.markedForRemoval = true;
        this.removedSyncerIds.push(syncer.id);
      } else {
        this.syncers = this.syncers.filter((s) => s !== syncer);
      }
    },
    buildNestedAttributes() {
      const attrs = [];
      // The single member-type choice is applied uniformly to every synced roster.
      const scope = this.scopeForMemberType(this.memberType);
      this.syncers.forEach((s) => {
        if (s.markedForRemoval) return;
        if (s.id) {
          attrs.push({ id: s.id, ...scope });
        } else {
          attrs.push({ roster_id: s.rosterId, ...scope });
        }
      });
      this.removedSyncerIds.forEach((id) => {
        attrs.push({ id, _destroy: true });
      });
      return attrs;
    },
    save() {
      const vm = this;
      if (vm.saving || !vm.hasPendingChanges) return;
      vm.error = false;
      vm.saving = true;

      const url = vm.$apiService.messageGroupUrl(vm.group.id);
      const params = {
        message_group: {
          message_group_roster_syncers_attributes: vm.buildNestedAttributes(),
        },
      };

      axios.put(url, params)
        .then(() => {
          vm.saving = false;
          vm.$notificationManager.emit('show-toast', 'Rosters updated', true);
          vm.$emit('saved');
          vm.$emit('close');
        })
        .catch((error) => {
          vm.saving = false;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    },
  },
};
</script>
